import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTaskById, updateTask, deleteTask } from '../../store/slices/taskSlice';
import StatusBadge from '../../components/StatusBadge';
import CommentSection from '../../components/CommentSection';
import Loading from '../../components/Loading';
import { TASK_STATUS, TASK_PRIORITY, COMMENT_ENTITY_TYPES } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const TaskDetailModal = ({ taskId, projectMembers, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadTask();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTask = async () => {
    setLoading(true);
    try {
      const result = await dispatch(fetchTaskById(taskId)).unwrap();
      setTask(result);
      setFormData({
        title: result.title,
        description: result.description || '',
        assigned_to: (result.assigned_to || []).map((emp) => emp._id),
        status: result.status,
        priority: result.priority,
        due_date: result.due_date ? result.due_date.substring(0, 10) : ''
      });
    } catch (error) {
      alert('Failed to load task details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'assigned_to') {
      const options = Array.from(e.target.selectedOptions, (option) => option.value);
      setFormData({ ...formData, assigned_to: options });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const submitData = {
        ...formData,
        due_date: formData.due_date || null
      };
      const result = await dispatch(updateTask({ id: taskId, data: submitData })).unwrap();
      setTask(result);
      setEditing(false);
      onSuccess();
    } catch (error) {
      alert('Failed to update task: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await dispatch(deleteTask(taskId)).unwrap();
      onSuccess();
      onClose();
    } catch (error) {
      alert('Failed to delete task: ' + error);
    }
  };

  const canDelete = task && (user?.role === 'admin' || task.created_by?._id === user?._id);

  if (loading) {
    return (
      <>
        <div className="modal-backdrop fade show" />
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-body">
                <Loading />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Task Details</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            {editing ? (
              <form onSubmit={handleUpdate}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Task Title *</label>
                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      name="description"
                      className="form-control"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Assign To (Hold Ctrl/Cmd to select multiple)</label>
                    <select
                      name="assigned_to"
                      className="form-select"
                      value={formData.assigned_to}
                      onChange={handleChange}
                      multiple
                      style={{ minHeight: '120px' }}
                    >
                      {(projectMembers || []).map((member) => (
                        <option key={member._id} value={member._id}>
                          {member.name} ({member.employee_code})
                        </option>
                      ))}
                    </select>
                    <small className="text-muted d-block mt-1">
                      Newly assigned members will receive an email notification.
                    </small>
                  </div>

                  <div className="row g-3">
                    <div className="col-sm-4">
                      <label className="form-label">Status *</label>
                      <select
                        name="status"
                        className="form-select"
                        value={formData.status}
                        onChange={handleChange}
                        required
                      >
                        {Object.values(TASK_STATUS).map((status) => (
                          <option key={status} value={status}>
                            {status.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label">Priority</label>
                      <select
                        name="priority"
                        className="form-select"
                        value={formData.priority}
                        onChange={handleChange}
                      >
                        {Object.values(TASK_PRIORITY).map((priority) => (
                          <option key={priority} value={priority}>
                            {priority}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label">Due Date</label>
                      <input
                        type="date"
                        name="due_date"
                        className="form-control"
                        value={formData.due_date}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="modal-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="fs-4 mb-2">{task.title}</h3>
                    <div className="d-flex gap-2">
                      <StatusBadge status={task.status} />
                      <StatusBadge status={task.priority} />
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <button className="btn btn-primary" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                    {canDelete && (
                      <button className="btn btn-outline-danger" onClick={handleDelete}>
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                {task.description && (
                  <p className="text-muted mb-0">{task.description}</p>
                )}

                <div className="row g-3 mt-3">
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">PROJECT</label>
                    <p className="mb-0">{task.project?.name || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">DUE DATE</label>
                    <p className="mb-0">{task.due_date ? new Date(task.due_date).toLocaleDateString() : 'Not specified'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">ASSIGNED TO</label>
                    <div className="d-flex gap-1 flex-wrap mt-1">
                      {(task.assigned_to || []).length === 0 ? (
                        <span className="text-muted small">Unassigned</span>
                      ) : (
                        task.assigned_to.map((emp) => (
                          <span key={emp._id} className="badge bg-primary" style={{ fontSize: '11px' }}>
                            {emp.name}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED BY</label>
                    <p className="mb-0">{task.created_by?.name || 'N/A'}</p>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="text-muted small fw-semibold">CREATED</label>
                    <p className="mb-0">{formatDateTime(task.created_at)}</p>
                  </div>
                </div>

                <hr />

                <CommentSection
                  entityType={COMMENT_ENTITY_TYPES.TASK}
                  entityId={taskId}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskDetailModal;
